import { Dispatch, SetStateAction } from 'react';
import { Col, Container, Row } from 'react-bootstrap';
import NoData from '../../../../../components/NoData';
import OpacityAnimation from '../../../../../components/OpacityAnimation';
import useGetGeneralAppData from '../../../../../hooks/useGetGeneralAppData';
import usaDateToBrDate from '../../../../../utils/usaDateToBrDate';
import { ArquivoBase, BaseDashDataType, DadosNotImpl, PeriodType, SelectedDashboardType } from '../../types';
import ImpAutCard from '../ImpAutCard';
import CheckingCard from './components/CheckingCard';
import DateInfoCard from './components/DateInfoCard';
import DownloadOtimizationBaseModal from './components/DownloadOtimizationBaseModal';
import EconomyCard from './components/EconomyCard';
import FinishCheckingModal from './components/FinishCheckingModal';
import FinishProcessModal from './components/FinishProcessModal';
import Header from './components/Header';
import OptimizedAndBalanceManagementCard from './components/OptimizedAndBalanceManagementCard';
import OptimizedAndDeployedCard from './components/OptimizedAndDeployedCard';
import OptimizedEmployeesCard from './components/OptimizedEmployeesCard';
import PurchaseCompareCard from './components/PurchaseCompareCard';
import RawEconomyInfo from './components/RawEconomyInfo';
import SavedPurchasesCard from './components/SavedPurchasesCard';

interface IBaseDashboard {
  selectedPeriod: PeriodType;
  setChangePeriodModalShow: Dispatch<SetStateAction<boolean>>;
  dashData: BaseDashDataType;
  finishProcessModalShow: boolean;
  setFinishProcessModalShow: Dispatch<SetStateAction<boolean>>;
  finishProcess: (baseFile: ArquivoBase[]) => Promise<void>;
  finishChecking: (baseFile: ArquivoBase[]) => Promise<void>;
  downloadOtimizationBaseModalShow: boolean;
  setDownloadOtimizationBaseModalShow: Dispatch<SetStateAction<boolean>>;
  setIsLoading: Dispatch<SetStateAction<boolean>>;
  setFinishCheckingModalShow: Dispatch<SetStateAction<boolean>>;
  finishCheckingModalShow: boolean;
  autImpData: DadosNotImpl[];
  loadImpAutData: (month?: number, year?: number) => Promise<void>;
  periodBeingSelectedAutImp: PeriodType;
  setPeriodBeingSelectedAutImp: Dispatch<SetStateAction<PeriodType>>;
  setSelectedPeriodAutImp: Dispatch<SetStateAction<PeriodType>>;
  changePeriodModalShow: boolean;
  selectedPeriodAutImp: PeriodType;
  selectedDashboard: SelectedDashboardType;
}

export default function BaseDashboard({
  selectedPeriod,
  setChangePeriodModalShow,
  dashData,
  finishProcessModalShow,
  setFinishProcessModalShow,
  finishProcess,
  downloadOtimizationBaseModalShow,
  setDownloadOtimizationBaseModalShow,
  setIsLoading,
  finishChecking,
  setFinishCheckingModalShow,
  finishCheckingModalShow,
  autImpData,
  loadImpAutData,
  periodBeingSelectedAutImp,
  setPeriodBeingSelectedAutImp,
  setSelectedPeriodAutImp,
  selectedPeriodAutImp,
  selectedDashboard,
}: IBaseDashboard) {
  const { isCustomer } = useGetGeneralAppData();

  return (
    <>
      <Header selectedPeriod={selectedPeriod} setChangePeriodModalShow={setChangePeriodModalShow} setDownloadOtimizationBaseModalShow={setDownloadOtimizationBaseModalShow} />

      {dashData.dadosDashBase && (
        <>
          <Container>
            <Row xs={1} md={2} lg={4}>
              <Col>
                <EconomyCard
                  initialCost={dashData.dadosDashBase.despesaInicial}
                  finalCost={dashData.dadosDashBase.despesaIdeal}
                  economyPercentage={dashData.dadosDashBase.percEconomia}
                />
              </Col>
              <Col>
                <PurchaseCompareCard
                  before={dashData.dadosDashBase.despesaInicial}
                  after={dashData.dadosDashBase.despesaIdeal}
                />
              </Col>
              <Col>
                <RawEconomyInfo
                  monthly={dashData.dadosDashBase.otimizacao}
                  yearly={dashData.dadosDashBase.economiaAnual}
                />
              </Col>
              <Col>
                <SavedPurchasesCard
                  saved={dashData.dadosDashBase.comprasEconomizadas}
                />
              </Col>
            </Row>

            <Row xs={1} md={2} lg={isCustomer ? 4 : 5}>
              <Col>
                <OptimizedEmployeesCard
                  optimized={dashData.dadosDashBase.totalOtimizados}
                  total={dashData.dadosDashBase.totalFuncionarios}
                />
              </Col>
              <Col>
                <OptimizedAndBalanceManagementCard
                  optimized={dashData.dadosDashBase.totalOtimizados}
                  balanceManagement={dashData.dadosDashBase.funcGestaoSaldo}
                />
              </Col>
              <Col>
                <OptimizedAndDeployedCard
                  optimized={dashData.dadosDashBase.totalOtimizados}
                  deployed={dashData.dadosDashBase.totalImplantados}
                />
              </Col>
              <Col>
                <CheckingCard
                  setFinishCheckingModalShow={setFinishCheckingModalShow}
                  checkingDate={dashData.dadosDashBase.dataConferenciaProces ? usaDateToBrDate(dashData.dadosDashBase.dataConferenciaProces) : undefined}
                />
              </Col>
              <Col>
                <DateInfoCard
                  startProcessDate={dashData.dadosDashBase.dataInicialProces ? usaDateToBrDate(dashData.dadosDashBase.dataInicialProces) : undefined}
                  endProcessDate={dashData.dadosDashBase.dataFinalProces ? usaDateToBrDate(dashData.dadosDashBase.dataFinalProces) : undefined}
                  processDuration={dashData.dadosDashBase.diasProcesso}
                  setFinishProcessModalShow={setFinishProcessModalShow}
                  checkingDate={dashData.dadosDashBase.dataConferenciaProces ? usaDateToBrDate(dashData.dadosDashBase.dataConferenciaProces) : undefined}
                />
              </Col>
            </Row>

            <Row>
              <Col>
                <OpacityAnimation>
                  <ImpAutCard
                    autImpData={autImpData}
                    loadImpAutData={loadImpAutData}
                    periodBeingSelectedAutImp={periodBeingSelectedAutImp}
                    setPeriodBeingSelectedAutImp={setPeriodBeingSelectedAutImp}
                    setSelectedPeriodAutImp={setSelectedPeriodAutImp}
                    selectedPeriodAutImp={selectedPeriodAutImp}
                    selectedDashboard={selectedDashboard}
                  />
                </OpacityAnimation>
              </Col>
            </Row>
          </Container>

          <FinishProcessModal
            finishProcessModalShow={finishProcessModalShow}
            setFinishProcessModalShow={setFinishProcessModalShow}
            finishProcess={(baseFiles) => finishProcess(baseFiles)}
            baseFiles={dashData.arquivoBase}
          />

          <FinishCheckingModal
            finishCheckingModalShow={finishCheckingModalShow}
            setFinishCheckingModalShow={setFinishCheckingModalShow}
            finishChecking={(baseFiles) => finishChecking(baseFiles)}
            baseFiles={dashData.arquivoBase}
          />

          <DownloadOtimizationBaseModal
            downloadModalShow={downloadOtimizationBaseModalShow}
            setDownloadModalShow={setDownloadOtimizationBaseModalShow}
            setIsLoading={setIsLoading}
          />
        </>
      )}

      {autImpData?.length !== 0 && !dashData.dadosDashBase && (
        <Container>
          <Row>
            <Col>
              <OpacityAnimation>
                <ImpAutCard
                  autImpData={autImpData}
                  loadImpAutData={loadImpAutData}
                  periodBeingSelectedAutImp={periodBeingSelectedAutImp}
                  setPeriodBeingSelectedAutImp={setPeriodBeingSelectedAutImp}
                  setSelectedPeriodAutImp={setSelectedPeriodAutImp}
                  selectedPeriodAutImp={selectedPeriodAutImp}
                  selectedDashboard={selectedDashboard}
                />
              </OpacityAnimation>
            </Col>
          </Row>
        </Container>
      )}

      {!dashData.dadosDashBase && (!autImpData || autImpData.length === 0) && (
        <NoData
          icon="emptyBox"
          label={(
            <>
              Não há dados de Gestão de Base para o período selecionado
            </>
          )}
        />
      )}
    </>
  );
}
